import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Auralis notification surface. One pill-soft tonal box used for inline
 * banners, error states, and contextual notices. Variants resolve to
 * semantic tokens so the panel automatically inverts under `.dark` and
 * under `.ads-auralis`. Use this in place of hand-rolled
 * `border-amber-200 bg-amber-50 …` boxes — those don't track theme.
 */
const notificationVariants = cva(
  "flex items-start gap-3 rounded-2xl border p-4 backdrop-blur-sm transition-colors",
  {
    variants: {
      variant: {
        info: "border-outline-variant/60 bg-surface-container/70 text-foreground",
        warning:
          "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
        success:
          "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        error:
          "border-destructive/30 bg-destructive/10 text-destructive dark:text-red-300",
      },
      size: {
        sm: "ds-caption p-3",
        md: "ds-body-sm p-4",
      },
    },
    defaultVariants: {
      variant: "info",
      size: "md",
    },
  },
);

const ICONS: Record<NonNullable<VariantProps<typeof notificationVariants>["variant"]>, React.ComponentType<{ className?: string }>> = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle2,
  error: AlertCircle,
};

export interface NotificationProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof notificationVariants> {
  title?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }> | false;
  onDismiss?: () => void;
}

const Notification = React.forwardRef<HTMLDivElement, NotificationProps>(
  ({ className, variant, size, title, icon, onDismiss, children, ...props }, ref) => {
    const resolvedVariant = variant ?? "info";
    const IconComponent = icon === false ? null : icon ?? ICONS[resolvedVariant];
    return (
      <div
        ref={ref}
        role={resolvedVariant === "error" || resolvedVariant === "warning" ? "alert" : "status"}
        className={cn(notificationVariants({ variant, size }), className)}
        {...props}
      >
        {IconComponent ? (
          <IconComponent className="mt-0.5 h-4 w-4 shrink-0" />
        ) : null}
        <div className="min-w-0 flex-1 space-y-1">
          {title ? <div className="ds-body-sm-strong text-foreground">{title}</div> : null}
          {children ? <div className="opacity-90">{children}</div> : null}
        </div>
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            className="ml-2 rounded-full p-1 text-current opacity-70 transition hover:bg-foreground/10 hover:opacity-100"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    );
  },
);
Notification.displayName = "Notification";

export { Notification, notificationVariants };
